import { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Search, X, Loader2, Sparkles } from 'lucide-react'
import { RootState } from '@/store/store'
import { startTextSearch, clearSearch, setTextQuery } from '@/store/slices/searchSlice'
import { useLazySearchByTextQuery } from '@/store/api'
import { setSearchResults, setSearchError } from '@/store/slices/searchSlice'

function SearchBar() {
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const { textQuery, isSearching, isSearchActive, mode } = useSelector(
    (state: RootState) => state.search
  )
  const [localQuery, setLocalQuery] = useState(textQuery)
  const [isFocused, setIsFocused] = useState(false)

  const [triggerSearch, { isLoading }] = useLazySearchByTextQuery()

  // Sync local query with redux state
  useEffect(() => {
    setLocalQuery(textQuery)
  }, [textQuery])

  const handleSearch = useCallback(async () => {
    const trimmedQuery = localQuery.trim()
    if (!trimmedQuery) return

    dispatch(startTextSearch(trimmedQuery))

    try {
      const results = await triggerSearch({ query: trimmedQuery, limit: 20 }).unwrap()
      dispatch(setSearchResults(results))
    } catch (error) {
      dispatch(setSearchError(error instanceof Error ? error.message : 'Search failed'))
    }
  }, [localQuery, dispatch, triggerSearch])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    } else if (e.key === 'Escape') {
      if (isSearchActive) {
        dispatch(clearSearch())
      } else {
        setLocalQuery('')
        inputRef.current?.blur()
      }
    }
  }, [handleSearch, isSearchActive, dispatch])

  const handleClear = useCallback(() => {
    setLocalQuery('')
    dispatch(clearSearch())
    inputRef.current?.focus()
  }, [dispatch])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalQuery(e.target.value)
    dispatch(setTextQuery(e.target.value))
  }, [dispatch])

  const showSearchActive = isSearchActive && mode === 'text'

  return (
    <div className="relative flex-1 max-w-2xl">
      <div
        className={`
          relative flex items-center transition-all duration-200
          ${isFocused || showSearchActive
            ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
            : ''
          }
          ${showSearchActive
            ? 'bg-primary/10 border-primary'
            : 'bg-secondary border-input'
          }
          border rounded-lg
        `}
      >
        {/* Search Icon / Loading Indicator */}
        <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
          {isLoading || isSearching ? (
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
          ) : (
            <Search
              className={`w-4 h-4 transition-colors ${
                showSearchActive ? 'text-primary' : 'text-muted-foreground'
              }`}
            />
          )}
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by description... (e.g., 'sunset over mountains')"
          value={localQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`
            flex-1 py-2.5 pr-2 bg-transparent border-none outline-none
            placeholder:text-muted-foreground text-sm
            ${showSearchActive ? 'text-foreground' : ''}
          `}
        />

        {/* Vector Search Badge */}
        {(isFocused || localQuery) && (
          <div className="hidden sm:flex items-center gap-1 px-2 py-1 mr-1 bg-primary/10 text-primary rounded text-xs">
            <Sparkles size={12} />
            <span>AI Search</span>
          </div>
        )}

        {/* Clear Button */}
        {(localQuery || showSearchActive) && (
          <button
            onClick={handleClear}
            className="flex items-center justify-center w-8 h-8 mr-1 text-muted-foreground hover:text-foreground rounded-md hover:bg-secondary/80 transition-colors"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}

        {/* Search Button */}
        {localQuery && !showSearchActive && (
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 mr-1 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Search size={14} />
            )}
            <span className="hidden sm:inline">Search</span>
          </button>
        )}
      </div>

      {/* Search Tips Dropdown */}
      {isFocused && !localQuery && (
        <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-primary" />
            <span className="text-xs text-zinc-400">AI-Powered Search Tips</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 rounded text-xs">Natural language</span>
              <span className="text-zinc-400">"a person walking on the beach"</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 rounded text-xs">Objects</span>
              <span className="text-zinc-400">"red car parked on street"</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 rounded text-xs">Scenes</span>
              <span className="text-zinc-400">"sunset over mountains"</span>
            </div>
          </div>
        </div>
      )}

      {/* Active Search Indicator */}
      {showSearchActive && (
        <div className="absolute -bottom-6 left-0 flex items-center gap-2 text-xs text-primary">
          <span>Showing results for: "{textQuery}"</span>
          <button
            onClick={handleClear}
            className="underline hover:no-underline"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  )
}

export default SearchBar
